import type { RequestInfo } from '@warp-drive/core/types/request';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildQueryRequestUrl } from '../utils/request-url.ts';

const accountTransactionUrl = (path: string) =>
  buildQueryRequestUrl('account-transactions', 'account-transactions', path);

interface ActionOptions {
  note: string;
}

interface UpdateAccountTransactionAttributes {
  amount?: number;
  transactionDate?: string;
  category?: string;
  transactionType?: string;
  feeAmount?: number;
  interestAmount?: number;
  principalAmount?: number;
  financialInstitutionId?: string;
}

export function updateAccountTransactionRequest(
  id: string,
  attributes: UpdateAccountTransactionAttributes
): RequestInfo {
  const { financialInstitutionId, ...plainAttributes } = attributes;

  const body = JSON.stringify({
    data: {
      id,
      type: 'account-transactions',
      attributes: plainAttributes,
      ...(financialInstitutionId
        ? {
            relationships: {
              financialInstitution: {
                data: { type: 'financial-institutions', id: financialInstitutionId },
              },
            },
          }
        : {}),
    },
  });
  return {
    url: accountTransactionUrl(id),
    method: 'PATCH',
    body,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function deleteAccountTransactionRequest(id: string): RequestInfo {
  return {
    url: accountTransactionUrl(id),
    method: 'DELETE',
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function verifyAccountTransactionRequest(id: string, options: ActionOptions): RequestInfo {
  const body = JSON.stringify(options);
  return {
    url: accountTransactionUrl(`${id}/transition/verification-state/verify`),
    method: 'PATCH',
    body,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function rejectAccountTransactionRequest(id: string, options: ActionOptions): RequestInfo {
  const body = JSON.stringify(options);
  return {
    url: accountTransactionUrl(`${id}/transition/verification-state/reject`),
    method: 'PATCH',
    body,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function requestVerificationAccountTransactionRequest(
  id: string,
  options: ActionOptions
): RequestInfo {
  const body = JSON.stringify(options);
  return {
    url: accountTransactionUrl(`${id}/transition/verification-state/request-verification`),
    method: 'PATCH',
    body,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

interface BulkAffectTransaction {
  id: string;
  attributes?: {
    financialInstitutionId?: string;
    amount?: number;
    transactionType?: string;
    category?: string;
  };
}

interface BulkAffectPayload {
  verify?: BulkAffectTransaction[];
  reject?: BulkAffectTransaction[];
  note?: string;
}

export function bulkAffectAccountTransactionsRequest(payload: BulkAffectPayload): RequestInfo {
  const body = JSON.stringify(payload);
  return {
    url: accountTransactionUrl('affect'),
    method: 'POST',
    body,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

interface BulkCreateTransaction {
  amount: number;
  transactionDate: string;
  transactionType: string;
  // Category is intentionally absent — the API derives it from transactionType.
  financialInstitutionId?: string;
}

interface BulkCreatePayload {
  observableDocumentId: string;
  personId: string;
  accountId?: string;
  financialInstitutionId?: string;
  transactions: BulkCreateTransaction[];
}

// Atomically create many account transactions linked to a single observable
// document. Backed by POST /account-transactions/bulk.
//
// Unlike the `affect` endpoint, this intentionally does NOT SkipCache: the
// created records must be put through the cache so `store.request(...).content.data`
// returns materialized records (with camelCase getters) for callers to render.
// SkipCache returns raw, unmaterialized JSON:API.
export function bulkCreateAccountTransactionsRequest(payload: BulkCreatePayload): RequestInfo {
  const body = JSON.stringify(payload);
  return {
    url: accountTransactionUrl('bulk'),
    method: 'POST',
    body,
  };
}
